import { useState, useRef, useCallback, useEffect } from 'react';
import { MAX_SPEED_MS, createSegmentFilter } from '@/lib/gpsFilter';

// Haversine formula to calculate distance between two GPS coordinates
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const useGPSTracking = () => {
  const [isTracking, setIsTracking] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('pending');
  const [currentPosition, setCurrentPosition] = useState(null);
  const [coordinates, setCoordinates] = useState([]);
  const [totalDistance, setTotalDistance] = useState(0);
  const [currentSpeed, setCurrentSpeed] = useState(0);
  const [maxSpeed, setMaxSpeed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [gpsSignalQuality, setGpsSignalQuality] = useState('searching');
  const [positionCount, setPositionCount] = useState(0);
  const [wakeLockActive, setWakeLockActive] = useState(false);
  const [backgroundWarning, setBackgroundWarning] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const watchIdRef = useRef(null);
  const timerRef = useRef(null);
  const lastPositionRef = useRef(null);
  const lastTimestampRef = useRef(null);
  const startTimeRef = useRef(null);
  const updateCountRef = useRef(0);
  const wakeLockRef = useRef(null);
  const isTrackingRef = useRef(false);
  const backgroundTimeRef = useRef(null);
  const liveDataRef = useRef({ totalDistance: 0, maxSpeed: 0, coordinates: [], duration: 0 });
  const sessionMetaRef = useRef(null);
  const snapshotTimerRef = useRef(null);
  const segmentFilterRef = useRef(createSegmentFilter());

  // GPS warm-up: skip first N readings (phone reports stale cached position initially)
  const GPS_WARMUP_COUNT = 3;
  // Auto-save live progress every 10s so a background kill loses at most 10s of data
  const SNAPSHOT_INTERVAL_MS = 10000;
  const ACTIVE_SESSION_KEY = 'dbpro_active_session';

  // ── Live progress snapshot (crash/kill recovery) ──
  const writeSnapshot = useCallback(() => {
    if (!isTrackingRef.current) return;
    try {
      localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify({
        ...(sessionMetaRef.current || {}),
        totalDistance: liveDataRef.current.totalDistance,
        duration: liveDataRef.current.duration,
        maxSpeed: liveDataRef.current.maxSpeed,
        coordinates: liveDataRef.current.coordinates,
        savedAt: new Date().toISOString()
      }));
      setLastSavedAt(Date.now());
    } catch (e) {
      // localStorage unavailable/full — non-fatal
    }
  }, []);

  // ── Wake Lock ──
  const requestWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) return;
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
      setWakeLockActive(true);
      wakeLockRef.current.addEventListener('release', () => {
        setWakeLockActive(false);
      });
    } catch (e) {
      // Wake lock can fail on low battery or unsupported contexts
      setWakeLockActive(false);
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
      setWakeLockActive(false);
    }
  }, []);

  // ── Visibility change: detect background/foreground ──
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // App went to background — record when and flush a snapshot immediately
        if (isTrackingRef.current) {
          backgroundTimeRef.current = Date.now();
          writeSnapshot();
        }
      } else if (document.visibilityState === 'visible') {
        // App came back to foreground
        if (isTrackingRef.current) {
          // Re-acquire wake lock (it auto-releases on background)
          requestWakeLock();

          // Check how long we were in background
          if (backgroundTimeRef.current) {
            const gapSeconds = Math.round((Date.now() - backgroundTimeRef.current) / 1000);
            if (gapSeconds > 5) {
              const mins = Math.floor(gapSeconds / 60);
              const secs = gapSeconds % 60;
              const timeStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
              setBackgroundWarning(
                `App was in background for ${timeStr}. GPS tracking may have gaps during that time.`
              );
              // Auto-dismiss after 8 seconds
              setTimeout(() => setBackgroundWarning(null), 8000);
            }
            backgroundTimeRef.current = null;
          }

          // Re-kick GPS in case it stalled
          if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
          }
          const options = { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 };
          watchIdRef.current = navigator.geolocation.watchPosition(
            handlePositionUpdate,
            handlePositionError,
            options
          );
        }
      }
    };

    const handlePageHide = () => {
      if (isTrackingRef.current) writeSnapshot();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [requestWakeLock, writeSnapshot]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check permission status on mount
  useEffect(() => {
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setPermissionStatus(result.state);
        result.onchange = () => {
          setPermissionStatus(result.state);
        };
      }).catch(() => {
        setPermissionStatus('pending');
      });
    }
  }, []);

  const resetMetrics = useCallback(() => {
    setCoordinates([]);
    setTotalDistance(0);
    setCurrentSpeed(0);
    setMaxSpeed(0);
    setDuration(0);
    setGpsSignalQuality('searching');
    setPositionCount(0);
    setBackgroundWarning(null);
    liveDataRef.current = { totalDistance: 0, maxSpeed: 0, coordinates: [], duration: 0 };
    segmentFilterRef.current = createSegmentFilter();
    updateCountRef.current = 0;
    backgroundTimeRef.current = null;
    lastPositionRef.current = null;
    lastTimestampRef.current = null;
    startTimeRef.current = null;
  }, []);

  const handlePositionUpdate = useCallback((position) => {
    const { latitude, longitude, accuracy: posAccuracy, speed } = position.coords;
    const timestamp = position.timestamp;

    setCurrentPosition({ latitude, longitude });
    setAccuracy(posAccuracy);
    setPositionCount(prev => prev + 1);
    setError(null);
    updateCountRef.current += 1;

    // Classify GPS signal quality based on accuracy
    if (posAccuracy <= 30) {
      setGpsSignalQuality('good');
    } else if (posAccuracy <= 50) {
      setGpsSignalQuality('fair');
    } else {
      setGpsSignalQuality('poor');
    }

    const newCoord = {
      latitude,
      longitude,
      timestamp: new Date(timestamp).toISOString(),
      accuracy: posAccuracy
    };

    setCoordinates(prev => [...prev, newCoord]);
    liveDataRef.current.coordinates.push(newCoord);

    // Calculate speed from GPS if available
    if (speed !== null && speed >= 0 && speed < MAX_SPEED_MS) {
      setCurrentSpeed(speed);
      setMaxSpeed(prev => Math.max(prev, speed));
      liveDataRef.current.maxSpeed = Math.max(liveDataRef.current.maxSpeed, speed);
    }

    // Calculate distance from last position
    // Skip first GPS_WARMUP_COUNT readings — phone reports stale cached position at startup
    if (lastPositionRef.current && updateCountRef.current > GPS_WARMUP_COUNT) {
      const distance = calculateDistance(
        lastPositionRef.current.latitude,
        lastPositionRef.current.longitude,
        latitude,
        longitude
      );

      // Check implied speed to reject GPS jumps
      const timeDiff = (timestamp - lastTimestampRef.current) / 1000;
      const impliedSpeed = timeDiff > 0 ? distance / timeDiff : 0;

      // Shared plausibility filter: distance/accuracy bounds, absolute speed cap,
      // and adaptive spike rejection vs recent steady pace
      if (segmentFilterRef.current(distance, timeDiff, posAccuracy)) {
        setTotalDistance(prev => prev + distance);
        liveDataRef.current.totalDistance += distance;

        if (speed === null || speed < 0) {
          setCurrentSpeed(impliedSpeed);
          setMaxSpeed(prev => Math.max(prev, impliedSpeed));
          liveDataRef.current.maxSpeed = Math.max(liveDataRef.current.maxSpeed, impliedSpeed);
        }
      }
    }

    lastPositionRef.current = { latitude, longitude };
    lastTimestampRef.current = timestamp;
  }, [maxSpeed]);

  const handlePositionError = useCallback((err) => {
    let errorMessage = 'Unable to retrieve location';
    switch (err.code) {
      case err.PERMISSION_DENIED:
        errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
        setPermissionStatus('denied');
        break;
      case err.POSITION_UNAVAILABLE:
        errorMessage = 'Location unavailable. Please ensure GPS is enabled and try opening the app in Chrome or Safari.';
        break;
      case err.TIMEOUT:
        errorMessage = 'Location request timed out. Please try opening this link in Chrome or Safari instead of in-app browsers.';
        break;
      default:
        errorMessage = `Location error: ${err.message}`;
    }
    setError(errorMessage);
  }, []);

  const startTracking = useCallback((sessionMeta = null) => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return false;
    }

    resetMetrics();
    sessionMetaRef.current = sessionMeta;
    setIsTracking(true);
    isTrackingRef.current = true;
    startTimeRef.current = Date.now();

    // Request wake lock to keep screen on
    requestWakeLock();

    // Start duration timer
    timerRef.current = setInterval(() => {
      if (startTimeRef.current) {
        const secs = Math.floor((Date.now() - startTimeRef.current) / 1000);
        liveDataRef.current.duration = secs;
        setDuration(secs);
      }
    }, 1000);

    // GPS options for high accuracy
    const options = {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 0
    };

    // Get initial position
    navigator.geolocation.getCurrentPosition(
      handlePositionUpdate,
      handlePositionError,
      options
    );

    // Start watching position
    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePositionUpdate,
      handlePositionError,
      options
    );

    // Auto-save progress every 10s while the session is active
    snapshotTimerRef.current = setInterval(writeSnapshot, SNAPSHOT_INTERVAL_MS);

    return true;
  }, [resetMetrics, handlePositionUpdate, handlePositionError, requestWakeLock, writeSnapshot]);

  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Session ended cleanly — stop snapshots and remove the recovery snapshot
    if (snapshotTimerRef.current) {
      clearInterval(snapshotTimerRef.current);
      snapshotTimerRef.current = null;
    }
    try {
      localStorage.removeItem(ACTIVE_SESSION_KEY);
    } catch (e) {
      // ignore
    }

    // Release wake lock
    releaseWakeLock();

    setIsTracking(false);
    isTrackingRef.current = false;
    setBackgroundWarning(null);

    return {
      coordinates,
      totalDistance,
      duration,
      averageSpeed: duration > 0 ? totalDistance / duration : 0,
      maxSpeed
    };
  }, [coordinates, totalDistance, duration, maxSpeed, releaseWakeLock]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (snapshotTimerRef.current) {
        clearInterval(snapshotTimerRef.current);
      }
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
      }
    };
  }, []);

  return {
    isTracking,
    permissionStatus,
    currentPosition,
    coordinates,
    totalDistance,
    currentSpeed,
    maxSpeed,
    duration,
    error,
    accuracy,
    gpsSignalQuality,
    positionCount,
    wakeLockActive,
    backgroundWarning,
    lastSavedAt,
    startTracking,
    stopTracking,
    resetMetrics
  };
};
